/* eslint-disable react-hooks/set-state-in-effect */
import { useState, useEffect, useCallback } from 'react'
import { kanbify } from '../lib/kanbifyClient'
import { logDevError } from '../lib/logger'

/* ========== PROFILES / LEADERBOARD ========== */
export function useProfiles(companyId) {
    const [profiles, setProfiles] = useState([])

    const fetch = useCallback(async () => {
        if (!companyId) { setProfiles([]); return }
        const { data } = await kanbify
            .from('profiles')
            .select('*, user_badges ( badge_key, awarded_at )')
            .eq('company_id', companyId)
            .eq('is_active', true)
            .order('points', { ascending: false })
        setProfiles(data || [])
    }, [companyId])

    useEffect(() => { fetch() }, [fetch])

    // Realtime on profiles - doar pentru compania curentă (5.2 optimizare)
    useEffect(() => {
        if (!companyId) return
        const channel = kanbify.channel(`profiles:${companyId}`)
            .on('postgres_changes', {
                event: 'UPDATE',
                schema: 'public',
                table: 'profiles',
                filter: `company_id=eq.${companyId}`
            }, () => { fetch() })
            .subscribe()
        return () => kanbify.removeChannel(channel)
    }, [companyId, fetch])

    return { profiles, fetch }
}

/* ========== DAILY ACTIVITY ========== */
export function useDailyActivity(userId) {
    const [todayActivity, setTodayActivity] = useState(null)
    const [weekActivity, setWeekActivity] = useState([])

    const fetch = useCallback(async () => {
        if (!userId) return
        const today = new Date().toISOString().split('T')[0]
        const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString().split('T')[0]
        const { data } = await kanbify
            .from('daily_activity')
            .select('*')
            .eq('user_id', userId)
            .gte('date', weekAgo)
            .order('date', { ascending: false })
        setWeekActivity(data || [])
        setTodayActivity(data?.find(d => d.date === today) || null)
    }, [userId])

    useEffect(() => { fetch() }, [fetch])
    return { todayActivity, weekActivity, fetch }
}

/* ========== GAMIFICATION (improved) ========== */
export async function awardPoints(userId, points, reason, cardId = null, isCardDone = false, isItemDone = false) {
    if (!userId) return null
    const { data } = await kanbify.rpc('award_points', {
        p_user_id: userId,
        p_points: points,
        p_reason: reason,
        p_card_id: cardId ?? null,
        p_is_card_done: isCardDone,
        p_is_item_done: isItemDone,
    })
    return data // returns { new_points, new_level, streak, badges_awarded }
}

export async function updateDailyTarget(userId, target) {
    const { error } = await kanbify.from('profiles').update({ daily_target: target }).eq('id', userId)
    if (error) logDevError('Failed to update daily target:', error.message)
}

export async function updateProfile(userId, updates) {
    const { data, error } = await kanbify.from('profiles').update(updates).eq('id', userId).select().single()
    return { data, error }
}
